import { useDrag } from "client/hooks/utils/useDrag";
import { inside } from "client/utils/collisionDetection";
import { deleteTextBox, setSettings } from "store/features/memeGenerator/actions";
import { TextBoxStates } from "store/features/memeGenerator/types";
import { getHighestZIndexElement } from "store/features/memeGenerator/utils/getHighestZIndexElement";
import {
    calculateNPosition,
    calculateNEPosition,
    calculateEPosition,
    calculateSEPosition,
    calculateSPosition,
    calculateSWPosition,
    calculateWPosition,
} from "store/features/memeGenerator/utils/useResizeHandlers";
import { store, useAppDispatch } from "store/store";
import { useMemeEditor } from "../../MemeEditorContext";

interface Options {
    onLeave?: () => void;
    onEnter?: () => void;
}

export const useTextBoxDrag = (textBoxId?: string, options: Options = {}) => {
    const dispatch = useAppDispatch();
    const { itemsContainerRef } = useMemeEditor();

    function getTextBox() {
        return store.getState().memeGenerator.meme?.textBoxes.find((_t) => _t.id === textBoxId);
    }

    function onPointerDown() {
        const highestZIndexElement = getHighestZIndexElement(store.getState().memeGenerator);
        if (!textBoxId || !highestZIndexElement || highestZIndexElement?.id === textBoxId) return;
        dispatch(
            setSettings({
                id: textBoxId,
                zIndex: highestZIndexElement.zIndex + 1,
                state: TextBoxStates.INSERT,
            }),
        );
    }

    function onPointerMove(left: number, top: number) {
        const textBox = getTextBox();
        if (!textBox) return;
        dispatch(
            setSettings({
                id: textBox.id,
                left: textBox.left + left,
                top: textBox.top + top,
            }),
        );
    }

    function onPointerUp() {
        const textBox = getTextBox();
        const itemsContainer = itemsContainerRef?.current;
        const memeSettings = store.getState().memeGenerator.meme?.settings;
        if (!textBox || !itemsContainer || !memeSettings) return;
        const { image } = memeSettings;
        const item = {
            left: textBox.left,
            top: textBox.top,
            width: textBox.width,
            height: textBox.height,
            rotation: textBox.rotation,
        };
        const [nX, nY] = calculateNPosition(item);
        const [neX, neY] = calculateNEPosition(item);
        const [wX, wY] = calculateWPosition(item);
        const [seX, seY] = calculateSEPosition(item);
        const [swX, swY] = calculateSWPosition(item);
        const [sX, sY] = calculateSPosition(item);
        const [eX, eY] = calculateEPosition(item);
        const textBoxPoints = [
            [image.left + textBox.left, -image.top - textBox.top],
            [image.left + nX, -image.top - nY],
            [image.left + neX, -image.top - neY],
            [image.left + eX, -image.top - eY],
            [image.left + seX, -image.top - seY],
            [image.left + sX, -image.top - sY],
            [image.left + swX, -image.top - swY],
            [image.left + wX, -image.top - wY],
        ];
        const boundarySlice = 20;
        const containerPoints = [
            [image.left + boundarySlice, -boundarySlice],
            [image.left + itemsContainer.getBoundingClientRect().width - 20, -boundarySlice],
            [
                image.left + itemsContainer.getBoundingClientRect().width - 20,
                -itemsContainer.getBoundingClientRect().height + boundarySlice,
            ],
            [image.left + boundarySlice, -itemsContainer.getBoundingClientRect().height + boundarySlice],
        ];
        const isInside = textBoxPoints.some((point) => inside(point, containerPoints));
        if (!isInside) {
            dispatch(deleteTextBox(textBox.id));
        }
    }

    const dragOptions: Parameters<typeof useDrag>[0] = {
        onPointerDown,
        onPointerMove,
        onPointerUp,
        onEnter: options.onEnter,
        onLeave: options.onLeave,
    };

    const { dragHandlers } = useDrag(dragOptions);

    return {
        dragEventHandlers: dragHandlers,
    };
};
